<template>
  <section class="mx-4 mb-6">
    <!-- Controles -->
    <v-card outlined class="elevation-0 mb-4">
      <div class="mc-controls pa-4">
        <div class="mc-title">
          <h3>{{ $t('ndfMonteCarlo.title') }}</h3>
          <p class="hint mb-0">
            {{ $t('ndfMonteCarlo.subtitle', { contracts: rows.length, name: portfolioName }) }}
          </p>
        </div>

        <div class="mc-fields">
          <v-select
            v-model="horizonDu"
            :items="horizonOptions"
            :label="$t('ndfMonteCarlo.controls.horizon')"
            dense outlined hide-details
            class="mc-field"
            @change="horizonTouched = true"
          />
          <v-select
            v-model="paths"
            :items="pathOptions"
            :label="$t('ndfMonteCarlo.controls.paths')"
            dense outlined hide-details
            class="mc-field"
          />
          <v-select
            v-model="volScale"
            :items="volOptions"
            :label="$t('ndfMonteCarlo.controls.vol')"
            dense outlined hide-details
            class="mc-field"
          />
          <base-button isActive :isDisable="running || !rows.length" @click="run">
            <v-icon small class="mr-2">mdi-play</v-icon>
            {{ running ? $t('ndfMonteCarlo.controls.running') : $t('ndfMonteCarlo.controls.run') }}
          </base-button>
        </div>
      </div>

      <v-progress-linear
        v-if="running"
        :value="progress * 100"
        color="#ffbf00"
        background-color="#232730"
        height="3"
      />

      <div v-if="result" class="mc-seed pa-4 pt-0">
        <span class="hint">
          {{ $t('ndfMonteCarlo.seedNote', { seed: result.seed, paths: formatInt(result.paths) }) }}
          {{ $t('ndfMonteCarlo.tailNote', { tail: formatInt(result.tailPaths) }) }}
        </span>
        <v-btn x-small text class="ml-2 seed-btn" @click="reseed">
          <v-icon x-small class="mr-1">mdi-dice-multiple-outline</v-icon>
          {{ $t('ndfMonteCarlo.controls.newSeed') }}
        </v-btn>
      </div>
    </v-card>

    <div v-if="!rows.length" class="empty-mc">{{ $t('ndfMonteCarlo.noContracts') }}</div>

    <template v-else-if="result">
      <!-- KPIs -->
      <div class="mtm-metrics mc-metrics">
        <div class="metric-card">
          <div class="metric-label">{{ $t('ndfMonteCarlo.kpi.currentMtm') }}</div>
          <div class="metric-value" :class="result.mtm0 >= 0 ? 'up' : 'down'">{{ money(result.mtm0) }}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">{{ $t('ndfMonteCarlo.kpi.expectedPnl') }}</div>
          <div class="metric-value">{{ money(result.mean) }}</div>
          <div class="hint">{{ $t('ndfMonteCarlo.kpi.standardError', { value: money(result.standardError, false) }) }}</div>
          <div class="hint">{{ $t('ndfMonteCarlo.kpi.deterministic', { value: money(deterministicPnl) }) }}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">{{ $t('ndfMonteCarlo.kpi.var95') }}</div>
          <div class="metric-value down">{{ money(result.var95, false) }}</div>
          <div class="hint">{{ $t('ndfMonteCarlo.kpi.var99', { value: money(result.var99, false) }) }}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">{{ $t('ndfMonteCarlo.kpi.cvar95') }}</div>
          <div class="metric-value down">{{ money(result.cvar95, false) }}</div>
          <div class="hint">{{ $t('ndfMonteCarlo.kpi.worst', { value: money(result.worst) }) }}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">{{ $t('ndfMonteCarlo.kpi.probLoss') }}</div>
          <div class="metric-value">{{ (result.probLoss * 100).toFixed(1) }}%</div>
        </div>
      </div>

      <v-row class="mt-1">
        <v-col cols="12" md="7">
          <v-card outlined class="elevation-0 fill-height">
            <v-card-title class="chart-title">{{ $t('ndfMonteCarlo.fanTitle') }}</v-card-title>
            <v-card-subtitle class="chart-sub">{{ $t('ndfMonteCarlo.fanSubtitle') }}</v-card-subtitle>
            <v-chart :option="fanOption" style="height: 34vh" autoresize />
          </v-card>
        </v-col>

        <v-col cols="12" md="5">
          <v-card outlined class="elevation-0 fill-height">
            <v-card-title class="chart-title">{{ $t('ndfMonteCarlo.histTitle') }}</v-card-title>
            <v-card-subtitle class="chart-sub">
              {{ $t('ndfMonteCarlo.histSubtitle', { horizon: result.horizonDu }) }}
            </v-card-subtitle>
            <v-chart :option="histOption" style="height: 34vh" autoresize />
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card outlined class="elevation-0">
            <v-card-title class="chart-title">{{ $t('ndfMonteCarlo.contribTitle') }}</v-card-title>
            <v-card-subtitle class="chart-sub">{{ $t('ndfMonteCarlo.contribSubtitle') }}</v-card-subtitle>
            <v-chart :option="contribOption" :style="contribStyle" autoresize />
            <div class="pa-4 pt-0 hint">{{ $t('ndfMonteCarlo.methodNote') }}</div>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </section>
</template>

<script>
import { mapStores } from 'pinia'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { usePortfolioStore } from '@/store/portfolioStore.js'
import { runMonteCarlo } from '@/utils/ndfMonteCarlo'

use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  BarChart,
  LineChart,
  CanvasRenderer,
])

const AXIS_COLOR = '#8b919c'
const GRID_COLOR = '#232730'
const ACCENT = '#ffbf00'
const UP = '#26c281'
const DOWN = '#e55353'

export default {
  name: 'NdfAnalytics',
  components: { VChart },
  data () {
    return {
      // Nasce indefinido: o padrao e o vencimento mais longo da carteira, que
      // so se conhece depois que as linhas chegam. `horizonTouched` distingue
      // "o usuario escolheu 21du" de "ainda esta no padrao" — sem isso, trocar
      // de portfolio ou descartaria a escolha dele ou congelaria o padrao no
      // teto da carteira anterior.
      horizonDu: null,
      horizonTouched: false,
      paths: 10000,
      volScale: 1,
      seed: null,
      running: false,
      progress: 0,
      result: null,
      // P&L que existiria com volatilidade zero: roll-down ao longo da curva
      // mais a captura do spread nos contratos que liquidam. E o motivo de o
      // P&L esperado NAO ser zero — sem isso o numero parece visao de mercado.
      deterministicPnl: 0,
      // Toda rodada carrega um token; se o portfolio mudar no meio, o
      // resultado da rodada velha e descartado em vez de sobrescrever o novo.
      runToken: 0,
    }
  },

  computed: {
    ...mapStores(usePortfolioStore),

    rows () {
      return this.portfolioStore.ndfRows
    },
    portfolioName () {
      return this.portfolioStore.selectedPortfolio?.name || '-'
    },
    currencyCode () {
      return this.portfolioStore.selectedPortfolio?.currency || 'BRL'
    },
    // Vencimento mais longo da carteira: e o teto natural do horizonte. Passado
    // ele todo contrato ja fixou, cada caminho esta congelado e esticar mais o
    // horizonte nao muda distribuicao nenhuma.
    maxDu () {
      return this.rows.reduce((m, r) => Math.max(m, r.du), 0)
    },
    horizonOptions () {
      const fixed = [
        { value: 1,   text: this.$t('ndfMonteCarlo.horizons.h1') },
        { value: 5,   text: this.$t('ndfMonteCarlo.horizons.h5') },
        { value: 21,  text: this.$t('ndfMonteCarlo.horizons.h21') },
        { value: 63,  text: this.$t('ndfMonteCarlo.horizons.h63') },
        { value: 126, text: this.$t('ndfMonteCarlo.horizons.h126') },
        { value: 252, text: this.$t('ndfMonteCarlo.horizons.h252') },
      ]
      const max = this.maxDu
      if (max <= 1) return [fixed[0]]
      return [
        ...fixed.filter(o => o.value < max),
        { value: max, text: this.$t('ndfMonteCarlo.horizons.lastMaturity', { du: max }) },
      ]
    },
    pathOptions () {
      // 500k e 1MM existem pela cauda: com 10k caminhos o CVaR 99% e a media de
      // 100 amostras. Para a media e o VaR 95% nao mudam nada perto do erro do
      // modelo.
      return [1000, 10000, 50000, 500000, 1000000].map(v => ({
        value: v,
        text: this.formatInt(v),
      }))
    },
    volOptions () {
      return [1, 1.5, 2].map(v => ({
        value: v,
        text: this.$t('ndfMonteCarlo.volOption', { scale: v.toFixed(1) }),
      }))
    },

    /* ---------- Fan chart ---------- */
    // As faixas sao series EMPILHADAS de deltas (p25-p5, p75-p25, p95-p75)
    // sobre uma base invisivel em p5 — o echarts nao tem banda nativa. Por isso
    // o tooltip le os percentis do `result.fan`, e nao do valor da serie, que
    // e um delta e nao significa nada sozinho.
    //
    // `stackStrategy: 'all'` e obrigatorio aqui: por padrao o echarts empilha
    // positivos e negativos em acumuladores SEPARADOS. Com o p5 negativo (que e
    // o caso sempre que a carteira pode dar prejuizo) os deltas positivos
    // empilhariam a partir do zero e a banda sairia deslocada — sem erro
    // nenhum, so um grafico errado.
    fanOption () {
      const fan = this.result.fan
      const steps = fan.map(f => f.step)
      const money = (v) => this.money(v)
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line' },
          formatter: (params) => {
            const i = params[0].dataIndex
            const f = fan[i]
            return [
              `<b>${this.$t('ndfMonteCarlo.axis.step', { step: f.step })}</b>`,
              `p95 ${money(f.p95)}`,
              `p75 ${money(f.p75)}`,
              `<b>p50 ${money(f.p50)}</b>`,
              `p25 ${money(f.p25)}`,
              `p5 &nbsp;${money(f.p5)}`,
            ].join('<br/>')
          },
        },
        legend: {
          data: [this.$t('ndfMonteCarlo.legend.band90'), this.$t('ndfMonteCarlo.legend.band50'), this.$t('ndfMonteCarlo.legend.median')],
          textStyle: { color: AXIS_COLOR },
          top: 0,
        },
        grid: { left: 70, right: 24, top: 34, bottom: 40 },
        xAxis: {
          type: 'category',
          data: steps,
          name: this.$t('ndfMonteCarlo.axis.businessDays'),
          nameLocation: 'middle',
          nameGap: 26,
          nameTextStyle: { color: AXIS_COLOR },
          axisLabel: { color: AXIS_COLOR },
          axisLine: { lineStyle: { color: GRID_COLOR } },
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: AXIS_COLOR, formatter: (v) => this.compact(v) },
          splitLine: { lineStyle: { color: GRID_COLOR } },
        },
        series: [
          {
            name: 'base', type: 'line', stack: 'fan', stackStrategy: 'all', symbol: 'none',
            lineStyle: { opacity: 0 }, itemStyle: { opacity: 0 },
            data: fan.map(f => f.p5), silent: true, tooltip: { show: false },
          },
          {
            name: this.$t('ndfMonteCarlo.legend.band90'), type: 'line', stack: 'fan', stackStrategy: 'all', symbol: 'none',
            lineStyle: { opacity: 0 },
            areaStyle: { color: 'rgba(255, 191, 0, 0.12)' },
            data: fan.map(f => f.p25 - f.p5),
          },
          {
            name: this.$t('ndfMonteCarlo.legend.band50'), type: 'line', stack: 'fan', stackStrategy: 'all', symbol: 'none',
            lineStyle: { opacity: 0 },
            areaStyle: { color: 'rgba(255, 191, 0, 0.3)' },
            data: fan.map(f => f.p75 - f.p25),
          },
          {
            // Fecha a faixa de 90% por cima; a legenda ja foi consumida pela
            // banda de baixo, entao esta nao aparece duas vezes.
            name: this.$t('ndfMonteCarlo.legend.band90'), type: 'line', stack: 'fan', stackStrategy: 'all', symbol: 'none',
            lineStyle: { opacity: 0 },
            areaStyle: { color: 'rgba(255, 191, 0, 0.12)' },
            data: fan.map(f => f.p95 - f.p75),
          },
          {
            name: this.$t('ndfMonteCarlo.legend.median'), type: 'line', symbol: 'none',
            lineStyle: { color: ACCENT, width: 2 },
            data: fan.map(f => f.p50),
            markLine: {
              silent: true, symbol: 'none',
              lineStyle: { color: AXIS_COLOR, type: 'dashed' },
              label: { color: AXIS_COLOR, formatter: this.$t('ndfMonteCarlo.legend.today') },
              data: [{ yAxis: this.result.mtm0 }],
            },
          },
        ],
      }
    },

    /* ---------- Histograma ---------- */
    histOption () {
      const bins = this.result.histogram
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const b = bins[params[0].dataIndex]
            return `${this.money(b.x0)} … ${this.money(b.x1)}<br/><b>${(b.pct * 100).toFixed(2)}%</b> ${this.$t('ndfMonteCarlo.axis.ofPaths')}`
          },
        },
        grid: { left: 50, right: 24, top: 20, bottom: 40 },
        xAxis: {
          type: 'category',
          data: bins.map(b => b.mid),
          name: this.$t('ndfMonteCarlo.axis.pnl'),
          nameLocation: 'middle',
          nameGap: 26,
          nameTextStyle: { color: AXIS_COLOR },
          axisLabel: {
            color: AXIS_COLOR,
            interval: Math.max(1, Math.floor(bins.length / 6)),
            formatter: (v) => this.compact(Number(v)),
          },
          axisLine: { lineStyle: { color: GRID_COLOR } },
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: AXIS_COLOR },
          splitLine: { lineStyle: { color: GRID_COLOR } },
        },
        series: [{
          type: 'bar',
          barCategoryGap: '0%',
          data: bins.map(b => ({
            value: b.count,
            itemStyle: { color: b.mid < 0 ? DOWN : UP, opacity: 0.75 },
          })),
          markLine: {
            silent: true, symbol: 'none',
            label: { color: AXIS_COLOR, fontSize: 10, position: 'insideEndTop' },
            data: [
              {
                xAxis: this.binIndexOf(-this.result.var95),
                lineStyle: { color: ACCENT, width: 2 },
                label: { formatter: `VaR 95%` },
              },
              {
                xAxis: this.binIndexOf(-this.result.cvar95),
                lineStyle: { color: DOWN, width: 2, type: 'dashed' },
                label: { formatter: `CVaR 95%` },
              },
            ],
          },
        }],
      }
    },

    /* ---------- Contribuicao para o CVaR ---------- */
    contribOption () {
      // De cima para baixo: quem mais machuca primeiro. O echarts desenha a
      // categoria 0 embaixo, entao a lista vai invertida.
      const data = [...this.result.byContract].reverse()
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const c = data[params[0].dataIndex]
            return [
              `<b>${this.contractLabel(c)}</b>`,
              `${this.$t('ndfMonteCarlo.contrib.tailPnl')}: ${this.money(c.tailPnl)}`,
              `${this.$t('ndfMonteCarlo.contrib.share')}: ${c.sharePct.toFixed(1)}%`,
              `${this.$t('ndfMonteCarlo.contrib.currentMtm')}: ${this.money(c.mtm0)}`,
              c.counterparty || '',
            ].filter(Boolean).join('<br/>')
          },
        },
        grid: { left: 150, right: 60, top: 10, bottom: 34 },
        xAxis: {
          type: 'value',
          axisLabel: { color: AXIS_COLOR, formatter: (v) => this.compact(v) },
          splitLine: { lineStyle: { color: GRID_COLOR } },
        },
        yAxis: {
          type: 'category',
          data: data.map(c => this.contractLabel(c)),
          axisLabel: { color: AXIS_COLOR, fontSize: 11 },
          axisLine: { lineStyle: { color: GRID_COLOR } },
        },
        series: [{
          type: 'bar',
          data: data.map(c => ({
            value: c.tailPnl,
            // Positivo no cenario ruim = a posicao PROTEGE. Verde e informacao,
            // nao decoracao.
            itemStyle: { color: c.tailPnl >= 0 ? UP : DOWN, opacity: 0.85 },
          })),
          label: {
            show: true,
            position: 'right',
            color: AXIS_COLOR,
            fontSize: 11,
            formatter: (p) => this.compact(p.value),
          },
        }],
      }
    },
    contribStyle () {
      return { height: `${Math.max(160, this.result.byContract.length * 34 + 60)}px` }
    },
  },

  watch: {
    // Trocar de portfolio invalida o resultado na hora — deixar o gráfico
    // antigo na tela sob o nome novo seria pior que ficar vazio.
    'portfolioStore.selectedPortfolioId': function () {
      this.result = null
      // Uma carteira mais curta pode nao alcancar o horizonte que estava
      // escolhido; sem isto o seletor ficaria mostrando um valor que nao esta
      // mais na lista.
      this.applyDefaultHorizon()
      this.run()
    },
  },

  mounted () {
    this.applyDefaultHorizon()
    this.run()
  },

  methods: {
    // Padrao = vencimento mais longo da carteira: e o unico horizonte em que a
    // carteira inteira ja liquidou, entao a distribuicao ali e o risco total do
    // book, nao um recorte. Escolha manual do usuario e preservada, so limitada
    // ao teto da carteira nova.
    applyDefaultHorizon () {
      const max = Math.max(1, this.maxDu)
      if (!this.horizonTouched || !this.horizonDu) this.horizonDu = max
      else if (this.horizonDu > max) this.horizonDu = max
    },
    reseed () {
      this.seed = null
      this.run()
    },
    async run () {
      if (!this.rows.length) { this.result = null; return }
      const token = ++this.runToken
      this.running = true
      this.progress = 0
      try {
        const result = await runMonteCarlo({
          rows: this.rows,
          curves: this.portfolioStore.ndfCurves,
          horizonDu: this.horizonDu,
          paths: this.paths,
          volScale: this.volScale,
          seed: this.seed,
          onProgress: (p) => { if (token === this.runToken) this.progress = p },
        })
        // Duas trajetorias sem aleatoriedade bastam: a vol zerada torna todos
        // os caminhos identicos.
        const deterministic = await runMonteCarlo({
          rows: this.rows,
          curves: this.portfolioStore.ndfCurves,
          horizonDu: this.horizonDu,
          paths: 2,
          volScale: 0,
          seed: result.seed,
        })
        if (token !== this.runToken) return
        this.result = result
        this.deterministicPnl = deterministic.mean
        this.seed = result.seed
      } finally {
        if (token === this.runToken) this.running = false
      }
    },

    binIndexOf (value) {
      const bins = this.result.histogram
      for (let i = 0; i < bins.length; i += 1) {
        if (value >= bins[i].x0 && value <= bins[i].x1) return i
      }
      return value < bins[0].x0 ? 0 : bins.length - 1
    },
    contractLabel (c) {
      const side = c.side === 'BUY' ? this.$t('ndfPositions.buy') : this.$t('ndfPositions.sell')
      return `${c.pair} ${side} · ${c.du}du`
    },
    formatInt (n) {
      try {
        return new Intl.NumberFormat('en-US').format(n)
      } catch (e) { return String(n) }
    },
    compact (value) {
      const n = Number(value) || 0
      const sign = n < 0 ? '-' : ''
      const abs = Math.abs(n)
      if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B`
      if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`
      if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(0)}K`
      return `${sign}${abs.toFixed(0)}`
    },
    money (value, withSign = true) {
      const n = Number(value) || 0
      const sign = withSign ? (n > 0 ? '+' : n < 0 ? '-' : '') : (n < 0 ? '-' : '')
      return `${sign}${this.currencyCode} ${this.compact(Math.abs(n))}`
    },
  },
}
</script>

<style scoped>
.mc-controls {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.mc-title h3 {
  font-size: 18px;
  font-weight: 800;
  color: var(--text);
  margin: 0 0 4px;
}
.mc-fields {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.mc-field {
  width: 190px;
}
.mc-seed {
  display: flex;
  align-items: center;
}
.seed-btn {
  text-transform: none;
  color: var(--muted) !important;
}

.mc-metrics {
  grid-template-columns: repeat(5, 1fr) !important;
}

.chart-title {
  font-size: 16px;
  font-weight: 800;
  color: var(--text);
  padding-bottom: 0;
}
.chart-sub {
  padding-top: 4px;
  color: var(--muted);
  font-size: 12px;
}

.empty-mc {
  color: var(--muted);
  text-align: center;
  padding: 48px 0;
}
</style>
